import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "./../backendconfig";
import "./RegisteredUsers.css";

const api = `${BACKEND_URL.replace(/\/+$/, "")}/users-api.php`;

export default function RegisteredUsers() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [request, setRequest] = useState({
    search: "",
    revision: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    async function loadUsers() {
      setLoading(true);
      setError("");

      try {
        const response = await fetch(
          `${api}?search=${encodeURIComponent(request.search)}`,
          {
            credentials: "include",
            cache: "no-store",
            signal: controller.signal,
          }
        );

        let data;

        try {
          data = await response.json();
        } catch {
          throw new Error(
            "Invalid API response. Check users-api.php and the backend URL."
          );
        }

        if (!response.ok || !data.ok) {
          throw new Error(data.message || "Unable to load users.");
        }

        if (!controller.signal.aborted) {
          setUsers(data.users);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setError(error.message);
          setUsers([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadUsers();

    return () => controller.abort();
  }, [request]);

  function runSearch(value) {
    setRequest((previous) => ({
      search: value.trim(),
      revision: previous.revision + 1,
    }));
  }

  return (
    <main className="ru-page">
      <section className="ru-panel">
        <header className="ru-heading">
          <div>
            <h1>Registered Users</h1>
            <p>View registered users and their contact details.</p>
          </div>

          <span className="ru-count">
            {loading
              ? "Loading…"
              : error
                ? "Unavailable"
                : `${users.length} ${
                    request.search ? "matching users" : "users"
                  }`}
          </span>
        </header>

        <form
          className="ru-search"
          onSubmit={(event) => {
            event.preventDefault();
            runSearch(search);
          }}
        >
          <div className="ru-search-field">
            <i className="bi bi-search" aria-hidden="true" />

            <input
              type="search"
              aria-label="Search registered users"
              placeholder="Search name, mobile, city, state or country"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <button type="submit" className="ru-button">
            Search
          </button>

          <button
            type="button"
            className="ru-button ru-reset"
            onClick={() => {
              setSearch("");
              runSearch("");
            }}
          >
            <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />
            Reset
          </button>
        </form>

        {error && (
          <div className="ru-error" role="alert">
            {error}
          </div>
        )}

        <div className="ru-table-scroll" aria-busy={loading}>
          <table className="ru-table">
            <thead>
              <tr>
                {[
                  "No.",
                  "Date",
                  "Name",
                  "Mobile",
                  "City",
                  "State",
                  "Country",
                  "Password",
                ].map((heading) => (
                  <th key={heading} scope="col">
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="ru-empty">
                    <span role="status">Loading users…</span>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} className="ru-empty">
                    User data could not be loaded.
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="ru-empty">
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((user, index) => (
                  <tr key={user.id}>
                    <td>{index + 1}</td>

                    <td className="ru-date">
                      {user.created_date ? (
                        <>
                          <span>{user.created_date}</span>
                          <small>{user.created_time}</small>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>

                    <td className="ru-name">{user.name}</td>

                    <td>
                      <a
                        href={`tel:${String(user.mobile).replace(
                          /[^+\d]/g,
                          ""
                        )}`}
                      >
                        {user.mobile}
                      </a>
                    </td>

                    <td>{user.city}</td>
                    <td>{user.state}</td>
                    <td>{user.country}</td>
                       <td>{user.password_hash}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}